import { useCallback } from 'react'
import { useNavigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useSegment } from '../../../../hooks'
import { getOrganizationViewUrl } from '../../../../utils/routing/getOrganizationViewUrl'
import type { QuoteConversionTrackingOption } from '../../types/quotes/quoteConversionTrackingOption'
import type { RelatedInvoice } from '../../types/relatedInvoice'
import { getRelatedInvoiceRoute } from '../../utils/depositInvoices/getRelatedInvoiceRoute'
import type { DiscountModeTrackingValue } from '../../utils/getDiscountModeTrackingValue'

interface UseConvertQuoteToInvoiceOptions {
  convertToInvoiceUrl: string | undefined
  existingInvoiceDraft?: RelatedInvoice
  onUpgradeClick?: () => void
  trackingDiscountMode?: DiscountModeTrackingValue
  trackingEventName?: string
  trackingOption?: QuoteConversionTrackingOption
}

export const useConvertQuoteToInvoice = ({
  convertToInvoiceUrl,
  existingInvoiceDraft,
  onUpgradeClick,
  trackingDiscountMode,
  trackingEventName,
  trackingOption,
}: UseConvertQuoteToInvoiceOptions) => {
  const navigate = useNavigate()
  const { organization } = useUserOrganization()
  const { track } = useSegment()

  const navigateToNewInvoice = useCallback(() => {
    if (convertToInvoiceUrl) {
      navigate(convertToInvoiceUrl)
    }
  }, [convertToInvoiceUrl, navigate])

  const onConvertClick = useCallback(() => {
    // A draft already exists for this quote - resume it instead of starting a new one.
    if (existingInvoiceDraft) {
      const pathname = getOrganizationViewUrl(organization?.url, getRelatedInvoiceRoute(existingInvoiceDraft))
      navigate(pathname)
      return
    }
    if (onUpgradeClick) {
      onUpgradeClick()
      return
    }
    if (trackingEventName) {
      track(trackingEventName, { discountMode: trackingDiscountMode, option: trackingOption })
    }
    navigateToNewInvoice()
  }, [
    existingInvoiceDraft,
    navigate,
    onUpgradeClick,
    navigateToNewInvoice,
    organization?.url,
    track,
    trackingDiscountMode,
    trackingEventName,
    trackingOption,
  ])

  return { onConvertClick }
}
